// Interaction state singleton with pub/sub pattern
// Manages UI mode, selection, drag state, and user interactions

/**
 * @typedef {'select' | 'pan' | 'draw-wall' | 'draw-opening' | 'draw-light' | 'draw-cover' | 'place-token' | 'place-starting-position' | 'attack-targeting'} InteractionMode
 */

/**
 * @typedef {import('../types/jsdoc-types.js').TokenDirection} TokenDirection
 */

/**
 * @typedef {object} AreaTargetingState
 * @property {boolean} isActive
 * @property {string|null} areaShape
 * @property {number} areaSize
 * @property {Array<{col: number, row: number}>} targetSquares
 */

/**
 * @typedef {object} SelectedElement
 * @property {'token' | 'wall' | 'opening' | 'light' | 'cover' | 'starting-position'} type
 * @property {string} id
 */

/**
 * @typedef {object} DragState
 * @property {boolean} isDragging
 * @property {number} startX
 * @property {number} startY
 * @property {number} currentX
 * @property {number} currentY
 * @property {'pan' | 'drag-element' | 'draw' | null} dragType
 * @property {SelectedElement|null} draggedElement
 */

/**
 * @typedef {object} HoverState
 * @property {number|null} col
 * @property {number|null} row
 * @property {SelectedElement|null} hoveredElement
 */

/**
 * @typedef {'hostile' | 'friendly' | 'any' | 'selfOrEmpty'} TargetingFilter
 */

/**
 * @typedef {object} AttackTargetingState
 * @property {string|null} attackerId - Token ID of the attacker
 * @property {string|null} actionId - Action being used
 * @property {Array<string>} validTargetIds - IDs of valid target tokens
 * @property {string|null} selectedTargetId - Currently selected target (kept for single-target back-compat)
 * @property {Array<string>} [selectedTargets] - Selected target IDs (multi-target actions); undefined after clear
 * @property {number} [maxTargets] - Max targets allowed (default 1)
 * @property {boolean} [allowRepeat] - Whether the same target can be selected more than once
 * @property {Array<{col: number, row: number}>} [targetSquares] - Grid squares within attack range
 * @property {TargetingFilter} [filter] - What kind of target the action looks for; drives render color
 * @property {string} [rangeType] - "reach" (melee) or "ranged"; drives the hover icon (swords vs target+arrow)
 * @property {string} [cursorBase] - server-picked cursor icon family (e.g. "swords" / "target" / "spell"); the client appends a filter color suffix to load the rasterized PNG
 * @property {number} [rangeNormalCells] - normal range in cells; cells beyond this but within rangeLongCells render the long-range cursor variant
 * @property {number} [rangeLongCells] - long range in cells (ranged only)
 * @property {number} [attackerCol] - attacker's column, for renderer distance math
 * @property {number} [attackerRow] - attacker's row, for renderer distance math
 */

/**
 * @typedef {object} PlacementState
 * @property {boolean} isPlacing
 * @property {string|null} characterId - Character being placed
 * @property {Array<{col: number, row: number}>} validPositions - Valid placement cells
 * @property {{ col: number, row: number, character: object }|null} shadowToken - Shadow token during placement
 */

/**
 * @typedef {object} DoorInteractionState
 * @property {boolean} showPanel
 * @property {string|null} openingId
 * @property {boolean} isOpen
 */

/**
 * Discriminator for which list (PCs or monster placements) an interaction is targeting.
 * @typedef {{ kind: 'pc' | 'monster', id: string }} EntityRef
 */

export class InteractionStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this.listeners = new Set();

    // Current mode
    /** @type {InteractionMode} */
    this.mode = 'select';

    // Selection
    /** @type {SelectedElement|null} */
    this.selectedElement = null;
    /** @type {Array<SelectedElement>} */
    this.selectedElements = [];

    // Drag state
    /** @type {DragState} */
    this.drag = {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      dragType: null,
      draggedElement: null,
    };

    // Hover state
    /** @type {HoverState} */
    this.hover = {
      col: null,
      row: null,
      hoveredElement: null,
    };

    // Attack targeting state
    /** @type {AttackTargetingState} */
    this.attackTargeting = {
      attackerId: null,
      actionId: null,
      validTargetIds: [],
      selectedTargetId: null,
    };

    // Area targeting state (AoE spells)
    /** @type {AreaTargetingState} */
    this.areaTargeting = {
      isActive: false,
      areaShape: null,
      areaSize: 0,
      targetSquares: [],
    };

    // PC placement state
    /** @type {PlacementState} */
    this.placement = {
      isPlacing: false,
      characterId: null,
      validPositions: [],
      /** @type {{ col: number, row: number, character: object } | null} */
      shadowToken: null,
    };

    /**
     * Remote players' placement hovers, keyed by playerId. Updated from
     * the per-game ephemeral SSE stream. Each entry carries enough info
     * to draw a ghost token at the hovered cell.
     * @type {Map<string, { col: number, row: number, characterId: string }>}
     */
    this.remotePlacementHovers = new Map();

    // Door interaction state
    /** @type {DoorInteractionState} */
    this.doorInteraction = {
      showPanel: false,
      openingId: null,
      isOpen: false,
    };

    // Drawing state (for editor)
    /** @type {Array<{x: number, y: number}>} */
    this.drawingPoints = [];

    // Movement trail state
    /** @type {Array<{x: number, y: number}>} */
    this.movementTrail = [];
    /** @type {EntityRef|null} */
    this.movingEntity = null;
    /** @type {{ col: number, row: number, direction: TokenDirection }|null} */
    this.movementPreview = null;

    // Direction drag state
    /** @type {boolean} */
    this.isDraggingDirection = false;
    /** @type {EntityRef|null} */
    this.directionDragEntity = null;
    /** @type {TokenDirection|null} */
    this.directionDragPreview = null;

    // Context menu state
    /** @type {boolean} */
    this.showContextMenu = false;
    /** @type {{x: number, y: number}} */
    this.contextMenuPosition = { x: 0, y: 0 };

    // Modal/panel state
    /** @type {boolean} */
    this.showCharacterSheet = false;
    /** @type {string|null} */
    this.characterSheetCharacterId = null;
    /** @type {boolean} */
    this.showGameMenu = false;
  }

  /**
   * Subscribe to state changes
   * @param {() => void} fn - Callback function
   * @returns {() => void} Unsubscribe function
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Notify all listeners of state change
   */
  notify() {
    this.listeners.forEach((fn) => {
      fn();
    });
  }

  /**
   * Set interaction mode
   * @param {InteractionMode} mode
   */
  setMode(mode) {
    this.mode = mode;
    // Clear selection when changing modes
    if (mode !== 'select') {
      this.clearSelection();
    }
    // Clear attack targeting if leaving that mode
    if (mode !== 'attack-targeting') {
      this.clearAttackTargeting();
    }
    this.notify();
  }

  /**
   * Select an element
   * @param {SelectedElement} element
   */
  select(element) {
    this.selectedElement = element;
    this.selectedElements = [element];
    this.notify();
  }

  /**
   * Add to selection (multi-select)
   * @param {SelectedElement} element
   */
  addToSelection(element) {
    if (!this.selectedElements.some((e) => e.type === element.type && e.id === element.id)) {
      this.selectedElements.push(element);
      this.selectedElement = element;
      this.notify();
    }
  }

  /**
   * Remove from selection
   * @param {SelectedElement} element
   */
  removeFromSelection(element) {
    this.selectedElements = this.selectedElements.filter((e) => !(e.type === element.type && e.id === element.id));
    if (this.selectedElement?.id === element.id) {
      this.selectedElement = this.selectedElements[0] || null;
    }
    this.notify();
  }

  /**
   * Clear selection
   */
  clearSelection() {
    this.selectedElement = null;
    this.selectedElements = [];
    this.notify();
  }

  /**
   * Check if element is selected
   * @param {string} type
   * @param {string} id
   * @returns {boolean}
   */
  isSelected(type, id) {
    return this.selectedElements.some((e) => e.type === type && e.id === id);
  }

  /**
   * Start dragging
   * @param {number} x
   * @param {number} y
   * @param {'pan' | 'drag-element' | 'draw'} dragType
   * @param {SelectedElement|null} [element]
   */
  startDrag(x, y, dragType, element = null) {
    this.drag = {
      isDragging: true,
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
      dragType,
      draggedElement: element,
    };
    this.notify();
  }

  /**
   * Update drag position
   * @param {number} x
   * @param {number} y
   */
  updateDrag(x, y) {
    if (this.drag.isDragging) {
      this.drag.currentX = x;
      this.drag.currentY = y;
      this.notify();
    }
  }

  /**
   * End dragging
   */
  endDrag() {
    this.drag = {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      dragType: null,
      draggedElement: null,
    };
    this.notify();
  }

  /**
   * Update hover state
   * @param {number|null} col
   * @param {number|null} row
   * @param {SelectedElement|null} [element]
   */
  setHover(col, row, element = null) {
    this.hover = {
      col,
      row,
      hoveredElement: element,
    };
    this.notify();
  }

  /**
   * Shared shape for attackTargeting state. Both token-targeting and AoE
   * targeting need the same base fields (notably `selectedTargets: []` and
   * `maxTargets: 1`) so the renderer can read `.length` and the targeting-
   * complete check works without each call site re-declaring them.
   * @param {{attackerId: string, actionId: string, filter?: TargetingFilter}} fields
   * @returns {AttackTargetingState}
   */
  _baseAttackTargeting({ attackerId, actionId, filter }) {
    return {
      attackerId,
      actionId,
      filter,
      validTargetIds: [],
      selectedTargetId: null,
      selectedTargets: [],
      maxTargets: 1,
    };
  }

  /**
   * Start attack targeting
   * @param {string} attackerId
   * @param {string} actionId
   * @param {Array<string>} validTargetIds
   * @param {{maxTargets?: number, allowRepeat?: boolean, filter?: TargetingFilter, rangeType?: string, cursorBase?: string, rangeNormalCells?: number, rangeLongCells?: number, attackerCol?: number, attackerRow?: number}} [options]
   */
  startAttackTargeting(attackerId, actionId, validTargetIds, options) {
    this.attackTargeting = {
      ...this._baseAttackTargeting({ attackerId, actionId, filter: options?.filter }),
      validTargetIds,
      maxTargets: options?.maxTargets ?? 1,
      allowRepeat: options?.allowRepeat ?? false,
      rangeType: options?.rangeType,
      cursorBase: options?.cursorBase,
      rangeNormalCells: options?.rangeNormalCells,
      rangeLongCells: options?.rangeLongCells,
      attackerCol: options?.attackerCol,
      attackerRow: options?.attackerRow,
    };
    this.mode = 'attack-targeting';
    this.notify();
  }

  /**
   * Select attack target. For multi-target actions, accumulates targets
   * until maxTargets is reached. For single-target actions, selects immediately.
   * @param {string} targetId
   */
  selectAttackTarget(targetId) {
    if (!this.attackTargeting.validTargetIds.includes(targetId)) {
      return;
    }
    if (this.attackTargeting.selectedTargets.length >= this.attackTargeting.maxTargets) {
      return;
    }
    if (!this.attackTargeting.allowRepeat && this.attackTargeting.selectedTargets.includes(targetId)) {
      return;
    }
    this.attackTargeting.selectedTargets.push(targetId);
    // Keep selectedTargetId for backward compatibility with single-target code
    this.attackTargeting.selectedTargetId = targetId;
    this.notify();
  }

  /**
   * Returns true when enough targets have been selected
   * @returns {boolean}
   */
  isTargetingComplete() {
    return this.attackTargeting.selectedTargets.length >= this.attackTargeting.maxTargets;
  }

  /**
   * Clear attack targeting
   */
  clearAttackTargeting() {
    this.attackTargeting = {
      attackerId: null,
      actionId: null,
      validTargetIds: [],
      selectedTargetId: null,
    };
    this.areaTargeting = {
      isActive: false,
      areaShape: null,
      areaSize: 0,
      targetSquares: [],
    };
    if (this.mode === 'attack-targeting') {
      this.mode = 'select';
    }
    this.notify();
  }

  /**
   * Start area targeting for AoE spells
   * @param {string} attackerId
   * @param {string} actionId
   * @param {Array<{col: number, row: number}>} targetSquares - valid squares to center the AoE
   * @param {string} areaShape - 'sphere', 'emanation', etc.
   * @param {number} areaSize - size in feet
   * @param {TargetingFilter} [filter] - drives template color (friendly → green, else red)
   */
  startAreaTargeting(attackerId, actionId, targetSquares, areaShape, areaSize, filter) {
    this.attackTargeting = {
      ...this._baseAttackTargeting({ attackerId, actionId, filter }),
      targetSquares,
    };
    this.areaTargeting = {
      isActive: true,
      areaShape,
      areaSize,
      targetSquares,
    };
    this.mode = 'attack-targeting';
    this.notify();
  }

  /**
   * Start PC placement
   * @param {string} characterId
   * @param {Array<{col: number, row: number}>} validPositions
   */
  startPlacement(characterId, validPositions) {
    this.placement = {
      isPlacing: true,
      characterId,
      validPositions,
      shadowToken: null,
    };
    this.notify();
  }

  /**
   * Update the set of valid placement cells while placement is in progress.
   * Used when another player places and a cell we were offering becomes
   * occupied — caller recomputes the valid set and pushes it here so the
   * UI re-renders without the stale cell.
   * @param {Array<{col: number, row: number}>} validPositions
   */
  setPlacementValidPositions(validPositions) {
    if (!this.placement.isPlacing) return;
    this.placement = { ...this.placement, validPositions };
    this.notify();
  }

  /**
   * End PC placement
   */
  endPlacement() {
    this.placement = {
      isPlacing: false,
      characterId: null,
      validPositions: [],
      shadowToken: null,
    };
    this.notify();
  }

  /**
   * Record a remote player's current placement hover. Driven by the
   * per-game ephemeral SSE stream.
   * @param {string} playerId
   * @param {{ col: number, row: number, characterId: string }} hover
   */
  setRemotePlacementHover(playerId, hover) {
    this.remotePlacementHovers.set(playerId, hover);
    this.notify();
  }

  /**
   * Drop a remote player's hover (e.g. on disconnect / placement done).
   * @param {string} playerId
   */
  clearRemotePlacementHover(playerId) {
    if (!this.remotePlacementHovers.has(playerId)) return;
    this.remotePlacementHovers.delete(playerId);
    this.notify();
  }

  /**
   * Update shadow token position during placement
   * @param {{ col: number, row: number, character: object } | null} shadowToken
   */
  setShadowToken(shadowToken) {
    this.placement.shadowToken = shadowToken;
  }

  /**
   * Check if position is valid for placement
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isValidPlacementPosition(col, row) {
    return this.placement.validPositions.some((p) => p.col === col && p.row === row);
  }

  /**
   * Show door interaction panel
   * @param {string} openingId
   * @param {boolean} isOpen
   */
  showDoorPanel(openingId, isOpen) {
    this.doorInteraction = {
      showPanel: true,
      openingId,
      isOpen,
    };
    this.notify();
  }

  /**
   * Hide door interaction panel
   */
  hideDoorPanel() {
    this.doorInteraction = {
      showPanel: false,
      openingId: null,
      isOpen: false,
    };
    this.notify();
  }

  /**
   * Toggle character sheet drawer
   * @param {string|null} [characterId]
   */
  toggleCharacterSheet(characterId = null) {
    if (characterId !== null) {
      this.showCharacterSheet = true;
      this.characterSheetCharacterId = characterId;
    } else {
      this.showCharacterSheet = !this.showCharacterSheet;
    }
    this.notify();
  }

  /**
   * Close character sheet drawer
   */
  closeCharacterSheet() {
    this.showCharacterSheet = false;
    this.characterSheetCharacterId = null;
    this.notify();
  }

  /**
   * Toggle game menu
   */
  toggleGameMenu() {
    this.showGameMenu = !this.showGameMenu;
    this.notify();
  }

  /**
   * Close game menu
   */
  closeGameMenu() {
    this.showGameMenu = false;
    this.notify();
  }

  /**
   * Start movement drag on a PC or monster placement
   * @param {EntityRef} entity
   * @param {{ x: number, y: number }} startPos - Pixel center of the entity's cell
   */
  startMovement(entity, startPos) {
    this.movingEntity = entity;
    this.movementTrail = [startPos];
    this.movementPreview = null;
    this.notify();
  }

  /**
   * Update movement trail and destination preview
   * Called on every mousemove during drag — does NOT notify (render loop reads directly)
   * @param {Array<{x: number, y: number}>} trail
   * @param {{ col: number, row: number, direction: TokenDirection }|null} preview
   */
  updateMovementTrail(trail, preview) {
    this.movementTrail = trail;
    this.movementPreview = preview;
  }

  /**
   * End movement drag
   */
  endMovement() {
    this.movingEntity = null;
    this.movementTrail = [];
    this.movementPreview = null;
    this.notify();
  }

  /**
   * Whether a movement drag is in progress
   * @returns {boolean}
   */
  get isMoving() {
    return this.movingEntity !== null;
  }

  /**
   * Start direction drag on a PC or monster placement
   * @param {EntityRef} entity
   * @param {TokenDirection} currentDirection
   */
  startDirectionDrag(entity, currentDirection) {
    this.isDraggingDirection = true;
    this.directionDragEntity = entity;
    this.directionDragPreview = currentDirection;
    this.notify();
  }

  /**
   * Update direction drag preview — called on mousemove, does NOT notify
   * @param {TokenDirection} direction
   */
  updateDirectionDragPreview(direction) {
    this.directionDragPreview = direction;
  }

  /**
   * End direction drag
   */
  endDirectionDrag() {
    this.isDraggingDirection = false;
    this.directionDragEntity = null;
    this.directionDragPreview = null;
    this.notify();
  }

  /**
   * Show context menu
   * @param {number} x
   * @param {number} y
   */
  showContextMenuAt(x, y) {
    this.showContextMenu = true;
    this.contextMenuPosition = { x, y };
    this.notify();
  }

  /**
   * Hide context menu
   */
  hideContextMenu() {
    this.showContextMenu = false;
    this.notify();
  }

  /**
   * Add drawing point (for editor)
   * @param {number} x
   * @param {number} y
   */
  addDrawingPoint(x, y) {
    this.drawingPoints.push({ x, y });
    this.notify();
  }

  /**
   * Clear drawing points
   */
  clearDrawingPoints() {
    this.drawingPoints = [];
    this.notify();
  }

  /**
   * Reset all interaction state
   */
  reset() {
    this.mode = 'select';
    this.selectedElement = null;
    this.selectedElements = [];
    this.drag = {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      dragType: null,
      draggedElement: null,
    };
    this.hover = {
      col: null,
      row: null,
      hoveredElement: null,
    };
    this.attackTargeting = {
      attackerId: null,
      actionId: null,
      validTargetIds: [],
      selectedTargetId: null,
    };
    this.placement = {
      isPlacing: false,
      characterId: null,
      validPositions: [],
      shadowToken: null,
    };
    this.doorInteraction = {
      showPanel: false,
      openingId: null,
      isOpen: false,
    };
    this.movementTrail = [];
    this.movingEntity = null;
    this.movementPreview = null;
    this.drawingPoints = [];
    this.showContextMenu = false;
    this.showCharacterSheet = false;
    this.characterSheetCharacterId = null;
    this.showGameMenu = false;
    this.notify();
  }
}

// Singleton instance
export const interactionState = new InteractionStateManager();
